import { InputController } from './InputController.js';
import { TiltController } from './TiltController.js';
import { PhysicsWorld } from './PhysicsWorld.js';
import { SceneBuilder } from './SceneBuilder.js';
import { BallController } from './BallController.js';
import { LevelLoader } from './LevelLoader.js';
import { OrientationController } from './OrientationController.js';
import { Timer } from './Timer.js';
import { BALL_IDLE_THRESHOLD_MS } from '../config.js';

/**
 * 协调所有游戏子系统。UIManager 通过回调与 Game 通信。
 */
export class Game {
  /**
   * @param {object} deps
   * @param {HTMLCanvasElement} deps.canvas
   * @param {(levelId: number, timeMs: number) => void} deps.onLevelComplete
   * @param {(levelId: number) => void} deps.onBallFell
   * @param {(message: string) => void} deps.onError
   * @param {() => void} [deps.onIdleWarning]
   * @param {() => void} [deps.onIdleWarningCleared]
   */
  constructor({ canvas, onLevelComplete, onBallFell, onError, onIdleWarning, onIdleWarningCleared }) {
    this.canvas = canvas;
    this.onLevelComplete = onLevelComplete;
    this.onBallFell = onBallFell;
    this.onError = onError;
    this.onIdleWarning = onIdleWarning || (() => {});
    this.onIdleWarningCleared = onIdleWarningCleared || (() => {});

    this.scene = new SceneBuilder(canvas);
    this.physics = new PhysicsWorld();
    this.input = new InputController(canvas);
    this.orientation = new OrientationController();
    this.tilt = new TiltController();
    this.timer = new Timer();
    this.loader = new LevelLoader();
    this.ball = new BallController({
      physics: this.physics,
      scene: this.scene,
      onLevelComplete: (ms) => this._handleLevelComplete(ms),
      onBallFell: () => this._handleBallFell(),
      getElapsedMs: () => this.timer.elapsedMs,
    });

    this.currentLevelId = null;
    this.currentLevel = null;
    this._running = false;
    this._lastFrameMs = 0;
    this._paused = false;
    this._idleAccumMs = 0;
    this._idleWarned = false;
  }

  async startLevel(levelId) {
    this.currentLevelId = levelId;
    try {
      this.currentLevel = await this.loader.load(levelId);
    } catch (e) {
      console.error(e);
      this.onError(`关卡加载失败：${e.message}`);
      return false;
    }
    this.physics.clear();
    this.physics.buildStaticGeometry(this.currentLevel);
    this.physics.spawnBall(this.currentLevel.start[0], this.currentLevel.start[1]);
    this.scene.buildLevel(this.currentLevel);
    this.scene.resetCamera();
    this.tilt.reset();
    this.timer.start();
    this.ball.startLevel(this.currentLevel);
    this._idleAccumMs = 0;
    this._idleWarned = false;
    this._start();
    return true;
  }

  restartCurrentLevel() {
    if (!this.currentLevel) return;
    this.tilt.reset();
    this.ball.restart();
    this._idleAccumMs = 0;
    if (this._idleWarned) {
      this._idleWarned = false;
      this.onIdleWarningCleared();
    }
  }

  exit() {
    this._stop();
    this.physics.clear();
    this.scene.clearLevel();
    this.tilt.reset();
    this.timer.reset();
    this.currentLevelId = null;
    this.currentLevel = null;
  }

  pause() {
    this._paused = true;
    this.timer.pause();
  }

  resume() {
    if (!this._paused) return;
    this._paused = false;
    this._lastFrameMs = performance.now();
    this.timer.resume();
  }

  _start() {
    if (this._running) return;
    this._running = true;
    this._lastFrameMs = performance.now();
    const tick = (nowMs) => {
      if (!this._running) return;
      const dt = Math.min(0.05, (nowMs - this._lastFrameMs) / 1000);
      this._lastFrameMs = nowMs;
      if (!this._paused) this._frame(dt, nowMs);
      this.scene.render();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  _stop() {
    this._running = false;
  }

  _frame(dt, nowMs) {
    const pointer = this.input.get();
    // 输入优先级：拖动中 → 指针；否则若陀螺仪启用 → 陀螺仪；都没有 → 回中
    let input;
    if (pointer.active) {
      input = pointer;
    } else if (this.orientation.isEnabled()) {
      input = this.orientation.get();
    } else {
      input = pointer; // active=false
    }
    this.tilt.update(dt, input);
    if (this.ball.isPhysicsActive()) {
      this.physics.setTilt(this.tilt.tiltX, this.tilt.tiltZ);
      this.physics.step(dt);
    }
    this.ball.update(dt, nowMs);
    this.timer.update(dt);
    this.scene.setTilt(this.tilt.tiltX, this.tilt.tiltZ);
    this.scene.syncBall(this.physics.ballBody);
    // 陀螺仪模式下用更竖直的俯角，方向对应更直观
    const gyroMode = this.orientation.isEnabled() && !pointer.active;
    this.scene.updateCamera(this.physics.ballBody, dt, { gyro: gyroMode });
    this._checkIdle(dt);
  }

  _checkIdle(dt) {
    const body = this.physics.ballBody;
    if (!body || !this.ball.isPhysicsActive()) {
      this._idleAccumMs = 0;
      if (this._idleWarned) {
        this._idleWarned = false;
        this.onIdleWarningCleared();
      }
      return;
    }
    const speed = body.velocity.length();
    if (speed < 0.05) {
      this._idleAccumMs += dt * 1000;
      if (!this._idleWarned && this._idleAccumMs > BALL_IDLE_THRESHOLD_MS) {
        this._idleWarned = true;
        this.onIdleWarning();
      }
    } else {
      if (this._idleWarned) {
        this._idleWarned = false;
        this.onIdleWarningCleared();
      }
      this._idleAccumMs = 0;
    }
  }

  _handleLevelComplete(timeMs) {
    this.timer.pause();
    this.onLevelComplete(this.currentLevelId, timeMs);
  }

  _handleBallFell() {
    this.onBallFell(this.currentLevelId);
  }

  getElapsedMs() {
    return this.timer.elapsedMs;
  }

  // ---- 陀螺仪控制 ----

  /**
   * 请求并启用陀螺仪输入。必须从用户手势上下文调用。
   * @returns {Promise<{ ok: boolean, reason?: string }>}
   */
  enableOrientation() {
    return this.orientation.enable();
  }

  disableOrientation() {
    this.orientation.disable();
  }

  isOrientationEnabled() {
    return this.orientation.isEnabled();
  }

  /** 以当前手机姿态作为新零点。 */
  calibrateOrientation() {
    this.orientation.calibrate();
  }
}
